import { useState } from "react";
import { toast } from "sonner";
import { Package } from "lucide-react";
import { authApi } from "../../lib/authApi";
import { OrderStatusBadge, StageTrack, OrderParts, ProgressList, OrderTimeline, fmtDate, fmtINR } from "./OrderShared";
import { QualitySummary, DeliverySummary } from "./QualityDeliverySummary";
import { ShipToForm, ConfirmDelivery } from "./CustomerDelivery";
import { OrderDocuments } from "./OrderDocuments";

const ASSIGNMENT_TEXT = {
  PENDING: "A verified manufacturing partner is reviewing your order.",
  ACCEPTED: "A verified manufacturing partner has accepted your order.",
};

export const OrdersSection = ({ orders, setOrders }) => {
  const upsert = (o) => setOrders && setOrders((prev) => prev.map((x) => (x.id === o.id ? o : x)));
  const [downloading, setDownloading] = useState(null);

  const download = (order) => async (f) => {
    setDownloading(f.id);
    try {
      const res = await authApi.get(`/orders/${order.id}/files/${f.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = f.name;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Download failed.");
    } finally {
      setDownloading(null);
    }
  };

  if (orders.length === 0) {
    return (
      <div className="p-10 rounded-xl bg-card border border-border text-center" data-testid="portal-orders-empty">
        <Package className="h-8 w-8 text-muted-foreground mx-auto mb-4" strokeWidth={1.5} />
        <h3 className="font-semibold font-heading">No orders yet</h3>
        <p className="mt-2 text-sm text-muted-foreground max-w-md mx-auto">Approve a firm quote on an RFQ and your order is created automatically — production tracking appears here.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="portal-orders-section">
      {orders.map((o) => (
        <div key={o.id} className="p-6 rounded-xl bg-card border border-border" data-testid={`portal-order-card-${o.id}`}>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-3">
              <span className="font-mono-tech text-sm font-bold text-primary" data-testid={`portal-order-number-${o.id}`}>{o.order_number}</span>
              <h4 className="font-semibold font-heading">{o.title}</h4>
              <OrderStatusBadge status={o.status} />
            </div>
            <span className="text-xs text-muted-foreground">{fmtDate(o.created_at)}</span>
          </div>
          <p className="mt-1 text-xs text-muted-foreground">From {o.rfq_number}{o.po_number ? ` · PO ${o.po_number}` : ""}</p>
          <div className="mt-4"><StageTrack status={o.status} heldFrom={o.held_from} /></div>
          <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm" data-testid={`portal-order-commercial-${o.id}`}>
            <span className="font-mono-tech text-lg font-bold">{o.commercial?.price != null ? fmtINR(o.commercial.price) : "—"}</span>
            <span className="text-muted-foreground">Lead time: <b>{o.lead_time_days ?? "—"} days</b></span>
            <span className="text-xs text-muted-foreground">Snapshot fixed at approval</span>
          </div>
          {o.status === "ON_HOLD" && <p className="mt-3 text-sm text-amber-600 dark:text-amber-400" data-testid={`portal-order-hold-${o.id}`}>On hold{o.hold_reason ? `: ${o.hold_reason}` : ""}</p>}
          {o.status === "CANCELLED" && <p className="mt-3 text-sm text-destructive" data-testid={`portal-order-cancel-${o.id}`}>Cancelled{o.cancel_reason ? `: ${o.cancel_reason}` : ""}</p>}
          {o.assignment && ASSIGNMENT_TEXT[o.assignment.status] && <p className="mt-3 text-sm text-muted-foreground" data-testid={`portal-order-assignment-${o.id}`}>{ASSIGNMENT_TEXT[o.assignment.status]}</p>}
          <OrderParts order={o} onDownload={download(o)} downloading={downloading} testPrefix="portal-order" />
          <ProgressList order={o} onDownload={download(o)} downloading={downloading} testPrefix="portal-order" />
          <QualitySummary order={o} viewer="customer" onDownload={download(o)} downloading={downloading} />
          <DeliverySummary order={o} viewer="customer" onDownload={download(o)} downloading={downloading} />
          <OrderDocuments order={o} role="customer" />
          {o.status !== "CANCELLED" && <ShipToForm key={`${o.id}-${o.delivery?.ship_to?.updated_at || "new"}`} order={o} onUpdated={upsert} />}
          <ConfirmDelivery order={o} onUpdated={upsert} />
          <OrderTimeline order={o} />
        </div>
      ))}
    </div>
  );
};
